import React from 'react';
import {
    Menu,
    MenuTrigger,
    MenuPopover,
    MenuList,
    MenuItem,
    MenuDivider,
} from '@fluentui/react-components';
import { ChevronDown16Regular, ChevronRight16Regular, MoreHorizontal16Regular } from '@fluentui/react-icons';
import styles from './css/index.module.css';
import RaciAssignmentCell from './RaciAssignmentCell';
import {
    ACTIVITY_COLUMN_MIN_WIDTH,
    STAKEHOLDER_COLUMN_WIDTH,
} from './raciConstants';
import RaciMatrixEmptyState from './RaciMatrixEmptyState';
import {
    activityHasAssignments,
    columnHasAssignments,
    getActivitiesForGroup,
    getAssignment,
    getSortedColumns,
    getSortedGroups,
    getUngroupedActivities,
    isRaciMatrixEmpty,
} from './raciMatrixModel';

const StakeholderColumnPlaceholderHeader = ({ readOnly, onStartAddColumn }) => (
    <th scope="col" className={styles.emptyStateHeaderStakeholder}>
        <div className={styles.emptyStateHeaderStakeholderInner}>
            {!readOnly && (
                <button type="button" className={styles.emptyStateTextAction} onClick={onStartAddColumn}>
                    + Add column
                </button>
            )}
            <span className={styles.emptyStateHeaderHint}>Role, team or stakeholder</span>
        </div>
    </th>
);

const ActivityMatrixRow = ({
    activity,
    matrix,
    columns,
    readOnly,
    addingColumn,
    showStakeholderPlaceholder,
    focusedCell,
    editingActivityId,
    moveToGroupItems,
    onStartEditActivity,
    onRenameActivity,
    onEndEditActivity,
    onCancelEditActivity,
    onFocusCell,
    onBlurCell,
    onAssignmentChange,
    onRequestDeleteActivity,
    onGroupMenuAction,
}) => (
        <tr className={styles.dataRow}>
            <th scope="row" className={styles.activityCell}>
                <div className={styles.activityCellInner}>
                    {!readOnly && (
                        <RowActionsMenu
                            onEdit={() => onStartEditActivity(activity.id)}
                            onMoveUp={() => onGroupMenuAction.moveActivityUp(activity.id)}
                            onMoveDown={() => onGroupMenuAction.moveActivityDown(activity.id)}
                            onDelete={() => onRequestDeleteActivity(activity)}
                            moveToGroupItems={moveToGroupItems}>
                            <button type="button" className={styles.rowMenuButton} aria-label="Activity actions">
                                <MoreHorizontal16Regular />
                            </button>
                        </RowActionsMenu>
                    )}
                    <ActivityNameCell
                        activity={activity}
                        readOnly={readOnly}
                        editingId={editingActivityId}
                        onStartEdit={onStartEditActivity}
                        onRename={onRenameActivity}
                        onEndEdit={onEndEditActivity}
                        onCancelEdit={onCancelEditActivity}
                    />
                </div>
            </th>
            {columns.map(col => {
                const value = getAssignment(matrix, activity.id, col.id);
                const focused = focusedCell?.activityId === activity.id && focusedCell?.columnId === col.id;
                return (
                    <RaciAssignmentCell
                        key={col.id}
                        value={value}
                        readOnly={readOnly}
                        focused={focused}
                        onFocus={() => onFocusCell(activity.id, col.id)}
                        onBlur={onBlurCell}
                        onChange={next => onAssignmentChange(activity.id, col.id, next)}
                    />
                );
            })}
            {showStakeholderPlaceholder && <td className={styles.matrixCellNoColumnPad} aria-hidden="true" />}
            {addingColumn && <td className={styles.matrixCell} />}
        </tr>
);

const ColumnHeaderCell = ({
    col,
    index,
    columnsLength,
    readOnly,
    onRenameColumn,
    onMoveColumnLeft,
    onMoveColumnRight,
    onRequestDeleteColumn,
}) => (
        <th scope="col" className={styles.headerStakeholder}>
            <div className={styles.headerStakeholderInner}>
                <span>{col.label}</span>
                {!readOnly && (
                    <Menu>
                        <MenuTrigger disableButtonEnhancement>
                            <button type="button" className={styles.columnMenuButton} aria-label={`${col.label} column actions`}>
                                <MoreHorizontal16Regular />
                            </button>
                        </MenuTrigger>
                        <MenuPopover>
                            <MenuList>
                                <MenuItem onClick={() => onRenameColumn(col.id)}>Rename column</MenuItem>
                                <MenuItem disabled={index === 0} onClick={() => onMoveColumnLeft(col.id)}>
                                    Move left
                                </MenuItem>
                                <MenuItem disabled={index === columnsLength - 1} onClick={() => onMoveColumnRight(col.id)}>
                                    Move right
                                </MenuItem>
                                <MenuDivider />
                                <MenuItem className={styles.menuItemDanger} onClick={() => onRequestDeleteColumn(col)}>
                                    Delete column
                                </MenuItem>
                            </MenuList>
                        </MenuPopover>
                    </Menu>
                )}
            </div>
        </th>
);

const GroupHeaderRow = ({
    group,
    activities,
    collapsed,
    colSpan,
    readOnly,
    onToggleGroupCollapsed,
    onAddActivityInGroup,
    onGroupMenuAction,
}) => {
    const countLabel =
        activities.length === 0 ? 'No activities' : activities.length === 1 ? '1 activity' : `${activities.length} activities`;

    return (
        <tr className={styles.groupRow}>
            <th colSpan={colSpan} className={styles.groupHeaderCell}>
                <div className={styles.groupHeaderInner}>
                    <button
                        type="button"
                        className={styles.groupCollapseButton}
                        onClick={() => onToggleGroupCollapsed(group.id, !collapsed)}
                        aria-expanded={!collapsed}>
                        {collapsed ? <ChevronRight16Regular /> : <ChevronDown16Regular />}
                    </button>
                    <span className={styles.groupName}>
                        {group.name || 'Untitled group'} · {countLabel}
                    </span>
                    {!readOnly && (
                        <Menu>
                            <MenuTrigger disableButtonEnhancement>
                                <button type="button" className={styles.groupMenuButton} aria-label="Group actions">
                                    <MoreHorizontal16Regular />
                                </button>
                            </MenuTrigger>
                            <MenuPopover>
                                <MenuList>
                                    <MenuItem onClick={() => onAddActivityInGroup(group.id)}>Add activity</MenuItem>
                                    <MenuItem onClick={() => onGroupMenuAction.startRenameGroup(group.id)}>Rename group</MenuItem>
                                    <MenuItem onClick={() => onGroupMenuAction.moveGroupUp(group.id)}>Move group up</MenuItem>
                                    <MenuItem onClick={() => onGroupMenuAction.moveGroupDown(group.id)}>Move group down</MenuItem>
                                    <MenuDivider />
                                    <MenuItem onClick={() => onGroupMenuAction.deleteGroup(group.id)}>Delete group</MenuItem>
                                </MenuList>
                            </MenuPopover>
                        </Menu>
                    )}
                </div>
            </th>
        </tr>
    );
};

const ActivityNameCell = ({ activity, readOnly, editingId, onStartEdit, onRename, onEndEdit, onCancelEdit }) => {
    const isEditing = editingId === activity.id;
    const inputRef = React.useRef(null);

    React.useEffect(() => {
        if (isEditing && inputRef.current) {
            inputRef.current.focus();
            inputRef.current.select();
        }
    }, [isEditing]);

    if (isEditing) {
        return (
            <input
                ref={inputRef}
                className={styles.activityInput}
                value={activity.name}
                placeholder="Enter activity or deliverable"
                onChange={e => onRename(activity.id, e.target.value)}
                onBlur={() => onEndEdit()}
                onKeyDown={e => {
                    if (e.key === 'Enter') {
                        e.preventDefault();
                        onEndEdit();
                    }
                    if (e.key === 'Escape') {
                        e.preventDefault();
                        onCancelEdit();
                    }
                }}
            />
        );
    }

    return (
        <button
            type="button"
            className={styles.activityNameButton}
            disabled={readOnly}
            onClick={() => !readOnly && onStartEdit(activity.id)}>
            {activity.name || <span className={styles.activityPlaceholder}>Enter activity or deliverable</span>}
        </button>
    );
};

const RowActionsMenu = ({ onEdit, onMoveUp, onMoveDown, onDelete, moveToGroupItems, children }) => (
    <Menu>
        <MenuTrigger disableButtonEnhancement>
            {children}
        </MenuTrigger>
        <MenuPopover>
            <MenuList>
                <MenuItem onClick={onEdit}>Edit activity</MenuItem>
                {moveToGroupItems.length > 0 && (
                    <Menu>
                        <MenuTrigger disableButtonEnhancement>
                            <MenuItem submenuIndicator>Move to group</MenuItem>
                        </MenuTrigger>
                        <MenuPopover>
                            <MenuList>
                                {moveToGroupItems.map(item => (
                                    <MenuItem key={item.key} onClick={item.onClick}>
                                        {item.label}
                                    </MenuItem>
                                ))}
                            </MenuList>
                        </MenuPopover>
                    </Menu>
                )}
                <MenuDivider />
                <MenuItem onClick={onMoveUp}>Move up</MenuItem>
                <MenuItem onClick={onMoveDown}>Move down</MenuItem>
                <MenuDivider />
                <MenuItem className={styles.menuItemDanger} onClick={onDelete}>Delete activity</MenuItem>
            </MenuList>
        </MenuPopover>
    </Menu>
);

const RaciMatrixTable = ({
    matrix,
    readOnly,
    addingColumn,
    newColumnLabel,
    onNewColumnLabelChange,
    onCommitNewColumn,
    onCancelNewColumn,
    focusedCell,
    onFocusCell,
    onBlurCell,
    onAssignmentChange,
    editingActivityId,
    onStartEditActivity,
    onRenameActivity,
    onEndEditActivity,
    onCancelEditActivity,
    onRequestDeleteActivity,
    onRequestDeleteColumn,
    onRenameColumn,
    onMoveColumnLeft,
    onMoveColumnRight,
    collapsedGroupIds,
    onToggleGroupCollapsed,
    onAddActivityInGroup,
    onGroupMenuAction,
    onAddActivity,
    onStartAddColumn,
}) => {
    if (isRaciMatrixEmpty(matrix)) {
        return (
            <RaciMatrixEmptyState
                readOnly={readOnly}
                addingColumn={addingColumn}
                newColumnLabel={newColumnLabel}
                onNewColumnLabelChange={onNewColumnLabelChange}
                onCommitNewColumn={onCommitNewColumn}
                onCancelNewColumn={onCancelNewColumn}
                onAddActivity={onAddActivity}
                onStartAddColumn={onStartAddColumn}
            />
        );
    }

    const columns = getSortedColumns(matrix);
    const sortedGroups = getSortedGroups(matrix);
    const ungrouped = getUngroupedActivities(matrix);
    const hasGroups = sortedGroups.length > 0;
    const showStakeholderPlaceholder = columns.length === 0 && !addingColumn;

    const buildMoveToGroupItems = activity => {
        const items = [];
        if (activity.groupId) {
            items.push({
                key: 'ungrouped',
                label: 'Ungrouped',
                onClick: () => onGroupMenuAction.moveActivityToGroup(activity.id, null),
            });
        }
        sortedGroups.forEach(g => {
            if (g.id !== activity.groupId) {
                items.push({
                    key: g.id,
                    label: g.name || 'Untitled group',
                    onClick: () => onGroupMenuAction.moveActivityToGroup(activity.id, g.id),
                });
            }
        });
        return items;
    };

    const colSpan = columns.length + 1 + (addingColumn ? 1 : 0) + (showStakeholderPlaceholder ? 1 : 0);

    const renderActivityRow = activity => (
        <ActivityMatrixRow
            key={activity.id}
            activity={activity}
            matrix={matrix}
            columns={columns}
            readOnly={readOnly}
            addingColumn={addingColumn}
            showStakeholderPlaceholder={showStakeholderPlaceholder}
            focusedCell={focusedCell}
            editingActivityId={editingActivityId}
            moveToGroupItems={buildMoveToGroupItems(activity)}
            onStartEditActivity={onStartEditActivity}
            onRenameActivity={onRenameActivity}
            onEndEditActivity={onEndEditActivity}
            onCancelEditActivity={onCancelEditActivity}
            onFocusCell={onFocusCell}
            onBlurCell={onBlurCell}
            onAssignmentChange={onAssignmentChange}
            onRequestDeleteActivity={onRequestDeleteActivity}
            onGroupMenuAction={onGroupMenuAction}
        />
    );

    const renderGroupSection = group => {
        const activities = getActivitiesForGroup(matrix, group.id);
        const collapsed = collapsedGroupIds.has(group.id);

        return (
            <React.Fragment key={group.id}>
                <GroupHeaderRow
                    group={group}
                    activities={activities}
                    collapsed={collapsed}
                    colSpan={colSpan}
                    readOnly={readOnly}
                    onToggleGroupCollapsed={onToggleGroupCollapsed}
                    onAddActivityInGroup={onAddActivityInGroup}
                    onGroupMenuAction={onGroupMenuAction}
                />
                {!collapsed && activities.length === 0 && !readOnly && (
                    <tr className={styles.emptyGroupRow}>
                        <th scope="row" className={styles.activityCell}>
                            <input
                                className={styles.activityInput}
                                placeholder="Enter activity or deliverable"
                                onKeyDown={e => {
                                    if (e.key === 'Enter') {
                                        e.preventDefault();
                                        const name = e.currentTarget.value.trim();
                                        if (name) {
                                            onGroupMenuAction.addActivityInGroupWithName(group.id, name);
                                            e.currentTarget.value = '';
                                        }
                                    }
                                }}
                            />
                        </th>
                        {columns.map(col => (
                            <td key={col.id} className={styles.matrixCell} />
                        ))}
                        {showStakeholderPlaceholder && <td className={styles.matrixCellNoColumnPad} aria-hidden="true" />}
                        {addingColumn && <td className={styles.matrixCell} />}
                    </tr>
                )}
                {!collapsed && activities.map(renderActivityRow)}
            </React.Fragment>
        );
    };

    return (
        <div className={styles.tableScroll}>
                <table className={styles.matrixTable} role="grid">
                    <colgroup>
                        <col style={{ minWidth: ACTIVITY_COLUMN_MIN_WIDTH, width: ACTIVITY_COLUMN_MIN_WIDTH }} />
                        {columns.map(col => (
                            <col key={col.id} style={{ width: STAKEHOLDER_COLUMN_WIDTH, minWidth: STAKEHOLDER_COLUMN_WIDTH }} />
                        ))}
                        {showStakeholderPlaceholder && (
                            <col style={{ width: STAKEHOLDER_COLUMN_WIDTH, minWidth: STAKEHOLDER_COLUMN_WIDTH }} />
                        )}
                        {addingColumn && <col style={{ width: STAKEHOLDER_COLUMN_WIDTH, minWidth: STAKEHOLDER_COLUMN_WIDTH }} />}
                    </colgroup>
                    <thead>
                        <tr>
                            <th scope="col" className={styles.headerActivity}>
                                Activity / Deliverable
                            </th>
                            {columns.map((col, index) => (
                                <ColumnHeaderCell
                                    key={col.id}
                                    col={col}
                                    index={index}
                                    columnsLength={columns.length}
                                    readOnly={readOnly}
                                    onRenameColumn={onRenameColumn}
                                    onMoveColumnLeft={onMoveColumnLeft}
                                    onMoveColumnRight={onMoveColumnRight}
                                    onRequestDeleteColumn={onRequestDeleteColumn}
                                />
                            ))}
                            {showStakeholderPlaceholder && (
                                <StakeholderColumnPlaceholderHeader
                                    readOnly={readOnly}
                                    onStartAddColumn={onStartAddColumn}
                                />
                            )}
                            {addingColumn && (
                                <th scope="col" className={styles.headerStakeholderAdding}>
                                    <input
                                        className={styles.newColumnInput}
                                        placeholder="Enter role, team or stakeholder"
                                        value={newColumnLabel}
                                        onChange={e => onNewColumnLabelChange(e.target.value)}
                                        onKeyDown={e => {
                                            if (e.key === 'Enter') {
                                                e.preventDefault();
                                                onCommitNewColumn();
                                            }
                                            if (e.key === 'Escape') {
                                                e.preventDefault();
                                                onCancelNewColumn();
                                            }
                                        }}
                                        onBlur={onCommitNewColumn}
                                        autoFocus
                                    />
                                </th>
                            )}
                        </tr>
                    </thead>
                    <tbody>
                        {sortedGroups.map(renderGroupSection)}
                        {hasGroups && (
                            <tr className={styles.sectionLabelRow}>
                                <th colSpan={colSpan} className={styles.sectionLabelCell}>
                                    Ungrouped
                                </th>
                            </tr>
                        )}
                        {ungrouped.map(renderActivityRow)}
                    </tbody>
                </table>
        </div>
    );
};

export { activityHasAssignments, columnHasAssignments };
export default RaciMatrixTable;
